import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { CalendarHeart, Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPrice, productCmsKey } from '@/lib/format'
import { useDialog } from '@/hooks/use-dialog'
import { useBag } from '@/components/generals/bag-provider'
import { ProductImage } from '@/components/generals/product-image'

interface ProductQuickViewProps {
    product: Product | null
    onClose: () => void
}

export const ProductQuickView: FC<ProductQuickViewProps> = ({ product, onClose }): ReactNode => {
    const { addItem, reserve } = useBag()
    const panelRef = useRef<HTMLDivElement>(null)
    const open = product !== null

    // Keep the last piece on screen while the dialog fades out.
    const [lastShown, setLastShown] = useState<Product | null>(null)

    useEffect(() => {
        if (product) setLastShown(product)
    }, [product])

    const shown = product ?? lastShown

    useDialog(open, onClose, panelRef)

    const details = shown
        ? [
            { label: "Material", column: 'material' as const, value: shown.material },
            { label: "Edition", column: 'edition' as const, value: shown.edition },
            { label: "Craft", column: 'craft_note' as const, value: shown.craft_note }
        ].filter((detail) => Boolean(detail.value))
        : []

    return (
        <div
            aria-hidden={!open}
            inert={!open}
            className={cn('fixed inset-0 z-[65] flex items-end justify-center p-2 sm:items-center sm:p-6', !open && 'pointer-events-none')}
        >
            <div
                onClick={onClose}
                className={cn('absolute inset-0 bg-espresso/45 backdrop-blur-sm transition-opacity duration-500', open ? 'opacity-100' : 'opacity-0')}
            />

            {shown && (
                <div
                    ref={panelRef}
                    role='dialog'
                    aria-modal='true'
                    aria-labelledby='quick-view-title'
                    tabIndex={-1}
                    className={cn(
                        'glass glass-frost relative grid max-h-[calc(100svh-1rem)] w-full max-w-5xl overflow-y-auto rounded-[2.25rem] text-espresso transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] focus:outline-none md:grid-cols-[1.05fr_1fr] md:overflow-hidden',
                        open ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                    )}
                >
                    <div className='relative aspect-[4/5] overflow-hidden bg-sand md:aspect-auto md:min-h-[38rem]'>
                        <ProductImage
                            src={shown.image}
                            alt={shown.image_alt ?? shown.name}
                            sizes='(min-width: 768px) 50vw, 100vw'
                        />

                        {shown.edition && (
                            <p className='glass glass-clear absolute left-5 top-5 rounded-full px-4 py-2 text-[0.62rem] uppercase tracking-[0.22em]'>
                                <span data-mdlfy-cms={productCmsKey(shown, 'edition')}>
                                    {shown.edition}
                                </span>
                            </p>
                        )}
                    </div>

                    <div className='flex flex-col p-7 sm:p-10 md:max-h-[calc(100svh-3rem)] md:overflow-y-auto'>
                        <div className='flex items-start justify-between gap-6'>
                            <p
                                data-mdlfy-cms={productCmsKey(shown, 'category')}
                                className='pt-3 text-[0.68rem] uppercase tracking-[0.28em] text-gold-deep'
                            >
                                {shown.category}
                            </p>

                            <button
                                type='button'
                                onClick={onClose}
                                aria-label="Close the details"
                                className='flex size-11 shrink-0 items-center justify-center rounded-full bg-white/60 ring-1 ring-espresso/10 transition-colors hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                            >
                                <X className='size-5' strokeWidth={1.5} />
                            </button>
                        </div>

                        <h2
                            id='quick-view-title'
                            data-mdlfy-cms={productCmsKey(shown, 'name')}
                            className='mt-4 font-heading text-[clamp(2.4rem,4vw,3.4rem)] font-light leading-[1]'
                        >
                            {shown.name}
                        </h2>

                        <p className='mt-4 font-heading text-3xl'>
                            {formatPrice(shown.price)}
                        </p>

                        {shown.description && (
                            <p
                                data-mdlfy-cms={productCmsKey(shown, 'description')}
                                className='mt-6 text-[0.98rem] leading-relaxed text-muted-foreground'
                            >
                                {shown.description}
                            </p>
                        )}

                        {details.length > 0 && (
                            <dl className='mt-8 divide-y divide-espresso/10 border-y border-espresso/10'>
                                {details.map((detail) => (
                                    <div key={detail.label} className='grid grid-cols-[6.5rem_1fr] gap-4 py-3.5'>
                                        <dt className='text-[0.66rem] uppercase tracking-[0.22em] text-espresso/55'>
                                            {detail.label}
                                        </dt>

                                        <dd data-mdlfy-cms={productCmsKey(shown, detail.column)} className='text-sm'>
                                            {detail.value}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        )}

                        <div className='mt-auto flex flex-col gap-3 pt-8 sm:flex-row'>
                            <button
                                type='button'
                                onClick={() => {
                                    addItem(shown)
                                    onClose()
                                }}
                                className='flex h-13 flex-1 items-center justify-center gap-2 rounded-full bg-espresso text-[0.74rem] uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                            >
                                <Plus className='size-4' strokeWidth={1.5} />
                                Add to bag
                            </button>

                            <button
                                type='button'
                                onClick={() => {
                                    onClose()
                                    reserve(shown)
                                }}
                                className='flex h-13 flex-1 items-center justify-center gap-2 rounded-full bg-white/60 text-[0.74rem] uppercase tracking-[0.18em] ring-1 ring-espresso/15 transition-colors hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                            >
                                <CalendarHeart className='size-4' strokeWidth={1.5} />
                                Private viewing
                            </button>
                        </div>

                        <p className='mt-4 text-xs leading-relaxed text-muted-foreground'>
                            Reserve online and a client advisor arranges payment, engraving and insured delivery with you personally.
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}
